<template>
  <v-flex xs12>
    <v-container fluid>
      <v-row align="center" class="mb-2">
        <v-btn icon @click="goBack">
          <v-icon>arrow_back</v-icon>
        </v-btn>
        <h3 class="ml-2">{{ $t('ESTIMATE_DETAIL_TITLE') }} #{{ id }}</h3>
        <v-chip v-if="estimate && !estimate.chained" small class="ml-3" color="grey lighten-2">
          {{ $t('ESTIMATE_RECALC_LABEL') }}
        </v-chip>
        <v-spacer></v-spacer>

        <v-menu offset-y>
          <template v-slot:activator="{ on }">
            <v-btn text :loading="downloading" v-on="on">
              <v-icon left>cloud_download</v-icon>{{ $t('ESTIMATE_EXPORT') }}
            </v-btn>
          </template>
          <v-list dense>
            <v-list-item @click="downloadOrderFile">
              <v-list-item-content>
                <v-list-item-title>{{ $t('ESTIMATE_DOWNLOAD_ORDER') }}</v-list-item-title>
                <v-list-item-subtitle>
                  {{ $t('ESTIMATE_DOWNLOAD_ORDER_HINT') }}
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
            <v-list-item @click="downloadReport">
              <v-list-item-content>
                <v-list-item-title>{{ $t('ESTIMATE_DOWNLOAD_REPORT') }}</v-list-item-title>
                <v-list-item-subtitle>
                  {{ $t('ESTIMATE_DOWNLOAD_REPORT_HINT') }}
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-menu>
      </v-row>

      <v-card v-if="estimate" flat light class="pa-4 mb-3">
        <v-row>
          <v-col cols="12" md="3">
            <div class="caption grey--text">{{ $t('ESTIMATE_PERIOD') }}</div>
            <div>
              {{ estimate.date_from | moment('DD/MM/YYYY HH:mm') }}
              &rarr;
              {{ estimate.date_to | moment('DD/MM/YYYY HH:mm') }}
            </div>
          </v-col>
          <v-col cols="6" md="2">
            <div class="caption grey--text">{{ $t('fields.propagandist') }}</div>
            <div>{{ estimate.propagandist }}</div>
          </v-col>
          <v-col cols="6" md="2">
            <div class="caption grey--text">{{ $t('ESTIMATE_MUST_GO_OUT') }}</div>
            <div class="title">{{ totals.qty_needed }}</div>
          </v-col>
          <v-col cols="6" md="2">
            <div class="caption grey--text">{{ $t('ESTIMATE_MISSING') }}</div>
            <div class="title" :class="totals.qty_missing > 0 ? 'red--text' : ''">
              {{ totals.qty_missing }}
            </div>
          </v-col>
          <v-col cols="6" md="3">
            <div class="caption grey--text">{{ $t('ESTIMATE_BACKLOG') }}</div>
            <div class="title">{{ totals.qty_backlog }}</div>
          </v-col>
        </v-row>
      </v-card>

      <v-card flat light>
        <v-tabs v-model="tab" light>
          <v-tab>{{ $t('ESTIMATE_TAB_BOOKS') }}</v-tab>
          <v-tab>{{ $t('ESTIMATE_TAB_ITEMS') }}</v-tab>
        </v-tabs>

        <v-tabs-items v-model="tab" light touchless>
          <v-tab-item>
            <v-row class="px-6 pt-4" align="center">
              <v-col cols="12" md="4">
                <v-text-field
                  v-model="bookFilters.search"
                  :label="$t('ESTIMATE_SEARCH_BOOK')"
                  prepend-inner-icon="search"
                  outlined
                  dense
                  hide-details
                  clearable
                />
              </v-col>
              <v-col cols="12" md="4">
                <v-switch
                  v-model="bookFilters.only_missing"
                  :label="$t('ESTIMATE_ONLY_MISSING')"
                  class="mt-0"
                  hide-details
                  dense
                />
              </v-col>
            </v-row>

            <v-data-table
              :headers="bookHeaders"
              :items="books"
              :server-items-length="booksTotal"
              :no-data-text="$t('NO_DATA')"
              hide-default-footer
              :options.sync="booksPagination"
              :loading="loadingBooks"
              class="elevation-1"
            >
              <template
                v-for="column in bookHintColumns"
                v-slot:[`header.${column}`]="{ header }"
              >
                <HeaderHint :key="column" :text="header.text" :hint="hints[column]" />
              </template>

              <template v-slot:[`item.qty_missing`]="{ item }">
                <span :class="item.qty_missing > 0 ? 'red--text font-weight-bold' : ''">
                  {{ item.qty_missing }}
                </span>
              </template>

              <template v-slot:[`item.coverage_pct`]="{ item }">
                <span v-if="item.coverage_pct !== null">{{ item.coverage_pct }}%</span>
                <span v-else>-</span>
              </template>

              <template v-slot:[`item.actions`]="{ item }">
                <v-btn icon :title="$t('ESTIMATE_SHOW_ITEMS')" @click="showItemsOfBook(item)">
                  <v-icon>list</v-icon>
                </v-btn>
              </template>
            </v-data-table>

            <Paginator
              :total-items="booksTotal"
              :page.sync="booksPagination.page"
              :items-per-page.sync="booksPagination.itemsPerPage"
              @change="fetchBooks"
            />
          </v-tab-item>

          <v-tab-item>
            <v-row class="px-6 pt-4" align="center">
              <v-col cols="12" md="4">
                <v-text-field
                  v-model="itemFilters.search"
                  :label="$t('ESTIMATE_SEARCH_ITEM')"
                  prepend-inner-icon="search"
                  outlined
                  dense
                  hide-details
                  clearable
                />
              </v-col>
              <v-col cols="12" md="5">
                <v-chip
                  v-if="itemFilters.book_id"
                  close
                  small
                  @click:close="clearBookFilter"
                >
                  {{ selectedBookLabel }}
                </v-chip>
              </v-col>
            </v-row>

            <v-data-table
              :headers="itemHeaders"
              :items="items"
              :server-items-length="itemsTotal"
              :no-data-text="$t('NO_DATA')"
              hide-default-footer
              :options.sync="itemsPagination"
              :loading="loadingItems"
              class="elevation-1"
            >
              <template
                v-for="column in itemHintColumns"
                v-slot:[`header.${column}`]="{ header }"
              >
                <HeaderHint :key="column" :text="header.text" :hint="hints[column]" />
              </template>

              <template v-slot:[`item.campaign_book_created_at`]="{ item }">
                {{ item.campaign_book_created_at | moment('DD/MM/YYYY') }}
              </template>
            </v-data-table>

            <Paginator
              :total-items="itemsTotal"
              :page.sync="itemsPagination.page"
              :items-per-page.sync="itemsPagination.itemsPerPage"
              @change="fetchItems"
            />
          </v-tab-item>
        </v-tabs-items>
      </v-card>
    </v-container>
  </v-flex>
</template>

<script>
  import { mapActions } from 'vuex';

  import { estimatesService } from '../../../utils/services/estimates.service';
  import HeaderHint from '../../../components/shared/HeaderHint.vue';
  import Paginator from '../../../components/shared/Paginator';
  import { routeNames } from '../../../config/router/routeNames';

  export default {
    name: 'CampaignReportEstimateDetail',
    components: { HeaderHint, Paginator },
    props: {
      id: {
        type: [String, Number],
        required: true,
      },
    },
    data() {
      return {
        tab: 0,
        estimate: null,
        downloading: false,
        loadingBooks: false,
        loadingItems: false,
        books: [],
        booksTotal: 0,
        items: [],
        itemsTotal: 0,
        selectedBookLabel: '',
        // L'ordinamento iniziale ricalca quello di default del backend, cosi'
        // la freccetta sull'header parte gia' sulla colonna giusta.
        booksPagination: {
          page: 1, itemsPerPage: 15, sortBy: ['title'], sortDesc: [false],
        },
        itemsPagination: {
          page: 1, itemsPerPage: 15, sortBy: ['teacher_name'], sortDesc: [false],
        },
        bookFilters: { search: null, only_missing: false },
        itemFilters: { search: null, book_id: null },
        bookHintColumns: [
          'qty_needed',
          'qty_backlog',
          'warehouse_qty',
          'warehouse_vqty',
          'qty_covered',
          'qty_missing',
          'coverage_pct',
          'teachers_count',
        ],
        itemHintColumns: [
          'qty_needed',
          'qty_processed',
          'booked_up_at_snapshot',
          'campaign_book_created_at',
        ],
        hints: {
          qty_needed: this.$t('ESTIMATE_HINT_MUST_GO_OUT'),
          qty_backlog: this.$t('ESTIMATE_HINT_BACKLOG'),
          warehouse_qty: this.$t('ESTIMATE_HINT_STOCK'),
          warehouse_vqty: this.$t('ESTIMATE_HINT_FREE'),
          qty_covered: this.$t('ESTIMATE_HINT_COVERED'),
          qty_missing: this.$t('ESTIMATE_HINT_MISSING'),
          coverage_pct: this.$t('ESTIMATE_HINT_COVERAGE'),
          teachers_count: this.$t('ESTIMATE_HINT_TEACHERS'),
          qty_processed: this.$t('ESTIMATE_HINT_PROCESSED'),
          booked_up_at_snapshot: this.$t('ESTIMATE_HINT_BOOKED'),
          campaign_book_created_at: this.$t('ESTIMATE_HINT_ASSIGNED_ON'),
        },
        bookHeaders: [
          { text: 'ISBN', value: 'isbn' },
          { text: this.$t('fields.title'), value: 'title' },
          { text: this.$t('ESTIMATE_MUST_GO_OUT'), value: 'qty_needed' },
          { text: this.$t('ESTIMATE_BACKLOG'), value: 'qty_backlog' },
          { text: this.$t('ESTIMATE_STOCK'), value: 'warehouse_qty' },
          { text: this.$t('ESTIMATE_FREE'), value: 'warehouse_vqty' },
          { text: this.$t('ESTIMATE_COVERED'), value: 'qty_covered' },
          { text: this.$t('ESTIMATE_MISSING'), value: 'qty_missing' },
          { text: this.$t('ESTIMATE_COVERAGE'), value: 'coverage_pct' },
          { text: this.$t('ESTIMATE_TEACHERS'), value: 'teachers_count' },
          { text: this.$t('fields.actions'), value: 'actions', sortable: false, align: 'center' },
        ],
        itemHeaders: [
          { text: this.$t('fields.teacher'), value: 'teacher_name' },
          { text: this.$t('fields.school'), value: 'school_name' },
          { text: 'ISBN', value: 'isbn' },
          { text: this.$t('fields.title'), value: 'title' },
          { text: this.$t('ESTIMATE_QTY_NEEDED'), value: 'qty_needed' },
          { text: this.$t('ESTIMATE_QTY_PROCESSED'), value: 'qty_processed' },
          { text: this.$t('ESTIMATE_BOOKED'), value: 'booked_up_at_snapshot' },
          { text: this.$t('ESTIMATE_ASSIGNED_ON'), value: 'campaign_book_created_at' },
        ],
      };
    },
    computed: {
      totals() {
        return (this.estimate && this.estimate.totals) || {
          qty_needed: 0,
          qty_missing: 0,
          qty_backlog: 0,
        };
      },
      bookRequestFilters() {
        return this.withSort(this.bookFilters, this.booksPagination);
      },
      itemRequestFilters() {
        return this.withSort(this.itemFilters, this.itemsPagination);
      },
    },
    methods: {
      ...mapActions('alert', ['showToast']),

      goBack() {
        this.$router.push({ name: routeNames.CAMPAIGN_REPORT_ESTIMATES });
      },

      /**
       * Le due tabelle sono server-side: senza sortBy/sortDesc nei filtri il
       * click sull'header non cambierebbe nulla.
       */
      withSort(filters, pagination) {
        const { sortBy, sortDesc } = pagination;
        const result = { ...filters };

        if (sortBy && sortDesc && sortBy.length === 1 && sortDesc.length === 1) {
          result.sortBy = sortBy;
          result.sortDesc = sortDesc;
        }

        return result;
      },

      saveFile(content, filename, type) {
        const link = document.createElement('a');
        const file = new Blob([content], { type });
        link.href = URL.createObjectURL(file);
        link.download = filename;
        link.click();
        URL.revokeObjectURL(link.href);
      },

      downloadOrderFile() {
        this.downloading = true;
        return estimatesService.exportOrderFile(this.id)
          .then(response => {
            if (!response.data) {
              this.showToast({ type: 'info', message: this.$t('ESTIMATE_NOTHING_MISSING') });
              return;
            }
            this.saveFile(response.data, `stima ${this.id}.txt`, 'text/plain');
          })
          .catch(error => {
            this.handleError(error, this.errors);
          })
          .finally(() => {
            this.downloading = false;
          });
      },

      downloadReport() {
        this.downloading = true;
        return estimatesService.exportReport(this.id)
          .then(response => {
            this.saveFile(response.data, `stima ${this.id}.csv`, 'text/csv');
          })
          .catch(error => {
            this.handleError(error, this.errors);
          })
          .finally(() => {
            this.downloading = false;
          });
      },

      showItemsOfBook(book) {
        this.itemFilters.book_id = book.book_id;
        this.selectedBookLabel = `${book.isbn} ${book.title}`;
        this.tab = 1;
      },

      clearBookFilter() {
        this.itemFilters.book_id = null;
        this.selectedBookLabel = '';
      },

      fetchEstimate() {
        return estimatesService.index({ filters: { estimate_id: this.id } })
          .then(response => {
            const rows = response.data.data;
            this.estimate = rows && rows.length ? rows[0] : null;
          })
          .catch(error => {
            this.handleError(error, this.errors);
          });
      },

      fetchBooks(forcePage) {
        this.loadingBooks = true;
        const { page, itemsPerPage } = this.booksPagination;
        return estimatesService.books(this.id, {
          page: forcePage || page || 1,
          itemsPerPage,
          filters: this.bookRequestFilters,
        })
          .then(response => {
            this.books = response.data.data;
            this.booksTotal = response.data.meta.total;
            this.booksPagination = this.getPagination(
              response.data.meta,
              { oldPagination: this.booksPagination, forcePage },
            );
          })
          .catch(error => {
            this.handleError(error, this.errors);
          })
          .finally(() => {
            this.loadingBooks = false;
          });
      },

      fetchItems(forcePage) {
        this.loadingItems = true;
        const { page, itemsPerPage } = this.itemsPagination;
        return estimatesService.items(this.id, {
          page: forcePage || page || 1,
          itemsPerPage,
          filters: this.itemRequestFilters,
        })
          .then(response => {
            this.items = response.data.data;
            this.itemsTotal = response.data.meta.total;
            this.itemsPagination = this.getPagination(
              response.data.meta,
              { oldPagination: this.itemsPagination, forcePage },
            );
          })
          .catch(error => {
            this.handleError(error, this.errors);
          })
          .finally(() => {
            this.loadingItems = false;
          });
      },
    },
    async created() {
      await this.fetchEstimate();
      await this.fetchBooks(1);
    },
    watch: {
      tab(value) {
        if (value === 1 && !this.items.length) {
          this.fetchItems(1);
        }
      },
      bookFilters: {
        deep: true,
        handler() {
          this.fetchBooks(1);
        },
      },
      itemFilters: {
        deep: true,
        handler() {
          this.fetchItems(1);
        },
      },
      // Vuetify rigenera l'array sortBy a ogni click sull'header, anche quando
      // cambia solo il verso: basta osservare questo per intercettarli tutti.
      'booksPagination.sortBy': {
        handler(newVal, oldVal) {
          if (newVal !== oldVal) {
            this.fetchBooks(1);
          }
        },
      },
      'itemsPagination.sortBy': {
        handler(newVal, oldVal) {
          if (newVal !== oldVal) {
            this.fetchItems(1);
          }
        },
      },
    },
  };
</script>
